import * as React from 'react';
import { Checkbox as AriaCheckbox, type CheckboxProps as AriaCheckboxProps } from 'react-aria-components';

import { cx } from '../../utils/cx';

export interface ICheckboxBaseProps {
  size?: 'sm' | 'md';
  className?: string;
  isFocusVisible?: boolean;
  isSelected?: boolean;
  isDisabled?: boolean;
  isIndeterminate?: boolean;
  isInvalid?: boolean;
}

export const CheckboxBase = ({
  className,
  isSelected,
  isDisabled,
  isIndeterminate,
  isInvalid,
  size = 'sm',
  isFocusVisible = false,
}: ICheckboxBaseProps) => {
  return (
    <div
      // `relative` is load-bearing: the checkmark + indeterminate SVGs below use
      // `position: absolute`. Without a positioned parent, their containing block
      // climbs to the initial containing block (the viewport). When an ancestor
      // (e.g. `body`) has `overflow: hidden` clipping in-flow descendants, the
      // SVGs paint *through* that clip — producing lone checkmarks floating
      // outside their cell. Discovered in mops-web's Releases scene where the
      // checklist extended past `body.clientHeight`. The underlying body-clip
      // architecture issue is tracked in ORN-548 (consumer app) and ORN-771
      // (mops-web); this `relative` is the cheap defense for the Checkbox so
      // future scenes can't repeat the bug regardless of how those land.
      className={cx(
        'bg-primary ring-primary relative flex size-4 shrink-0 cursor-pointer appearance-none items-center justify-center rounded ring-1 ring-inset',
        size === 'md' && 'size-5 rounded-md',
        (isSelected || isIndeterminate) && 'bg-brand-solid ring-bg-brand-solid',
        isInvalid && !isDisabled && !isSelected && !isIndeterminate && 'ring-error_subtle',
        isInvalid && isFocusVisible && !isDisabled && 'ring-error ring-2',
        isDisabled && 'bg-disabled_subtle ring-disabled cursor-not-allowed',
        isFocusVisible && !isInvalid && 'outline-focus-ring outline-2 outline-offset-2',
        className
      )}
    >
      <svg
        aria-hidden="true"
        viewBox="0 0 14 14"
        fill="none"
        className={cx(
          'text-fg-white transition-inherit-all pointer-events-none absolute h-3 w-2.5 opacity-0',
          size === 'md' && 'size-3.5',
          isIndeterminate && 'opacity-100',
          isDisabled && 'text-fg-disabled_subtle'
        )}
      >
        <path
          d="M2.91675 7H11.0834"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>

      <svg
        aria-hidden="true"
        viewBox="0 0 14 14"
        fill="none"
        className={cx(
          'text-fg-white transition-inherit-all pointer-events-none absolute size-3 opacity-0',
          size === 'md' && 'size-3.5',
          isSelected && !isIndeterminate && 'opacity-100',
          isDisabled && 'text-fg-disabled_subtle'
        )}
      >
        <path
          d="M11.6666 3.5L5.24992 9.91667L2.33325 7"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </div>
  );
};
CheckboxBase.displayName = 'CheckboxBase';

interface ICheckboxProps extends AriaCheckboxProps {
  ref?: React.Ref<HTMLLabelElement>;
  size?: 'sm' | 'md';
  label?: React.ReactNode;
  hint?: React.ReactNode;
  /** Error text rendered beneath the label. Pair with `isInvalid` to surface the red affordance. */
  errorMessage?: React.ReactNode;
}

const Checkbox = ({ label, hint, errorMessage, size = 'sm', className, ...ariaCheckboxProps }: ICheckboxProps) => {
  const sizes = {
    sm: {
      root: 'gap-2',
      textWrapper: '',
      label: 'text-sm font-semibold',
      hint: 'text-sm',
    },
    md: {
      root: 'gap-3',
      textWrapper: 'gap-0.5',
      label: 'text-md font-semibold',
      hint: 'text-md',
    },
  };

  return (
    <AriaCheckbox
      {...ariaCheckboxProps}
      className={(state) =>
        cx(
          'flex items-start',
          state.isDisabled && 'cursor-not-allowed',
          sizes[size].root,
          typeof className === 'function' ? className(state) : className
        )
      }
    >
      {({ isSelected, isIndeterminate, isDisabled, isFocusVisible, isInvalid }) => (
        <>
          <CheckboxBase
            size={size}
            isSelected={isSelected}
            isIndeterminate={isIndeterminate}
            isDisabled={isDisabled}
            isInvalid={isInvalid}
            isFocusVisible={isFocusVisible}
            className={label || hint || errorMessage ? 'mt-0.5' : ''}
          />
          {(label || hint || errorMessage) && (
            <div className={cx('inline-flex flex-col', sizes[size].textWrapper)}>
              {label && <p className={cx('text-secondary select-none', sizes[size].label)}>{label}</p>}
              {hint && (
                <span className={cx('text-tertiary', sizes[size].hint)} onClick={(event) => event.stopPropagation()}>
                  {hint}
                </span>
              )}
              {isInvalid && errorMessage && (
                <span className={cx('text-error-primary mt-0.5', sizes[size].hint)} role="alert">
                  {errorMessage}
                </span>
              )}
            </div>
          )}
        </>
      )}
    </AriaCheckbox>
  );
};
Checkbox.displayName = 'Checkbox';

export default Checkbox;
